import { useState } from "react";
import { Link } from "react-router-dom";
import { PageRenderer } from "@/components/sections/PageRenderer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { track, EVENT } from "@/lib/track";
import { LifeBuoy, CheckCircle2, Paperclip } from "lucide-react";

const CATEGORIES = [
  "App Login Issue", "Booking Issue", "Billing / Payment Issue", "Package / Membership Question",
  "Cancellation / Rescheduling", "Mavs Volleyball Support", "Online Training Support",
  "Event / Camp Registration", "General Question", "Technical Issue", "Refund / Credit Request", "Other",
];
const URGENCY = ["Low", "Normal", "Urgent"];
const CONTACT_METHODS = ["Email", "Phone", "Text"];

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", client_name: "", category: "General Question", urgency: "Normal", message: "", attachment_url: "", preferred_contact: "Email" };

export default function Support() { return <PageRenderer pageKey="support" />; }

export const SupportBody = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.message) { toast.error("Please complete the required fields"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/support/tickets", form);
      track(EVENT.SUPPORT_TICKET_SUBMIT, form.category);
      track(EVENT.FORM_SUBMIT, "Support Ticket");
      toast.success(data.message || "Ticket submitted");
      setForm(EMPTY); setDone(true);
    } catch (err) { toast.error("Could not submit ticket. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="support-page">
      {/* hero via PageRenderer */}
      <section id="support-form" className="scroll-mt-24 bg-background py-16 sm:py-20">
        <div className="mx-auto w-full max-w-2xl px-4 sm:px-6">
          <div className="mb-8 flex items-center gap-3"><LifeBuoy className="h-8 w-8 text-brand" /><p className="text-muted-foreground">Tell us what's going on and how you'd like to be reached.</p></div>

          {done ? (
            <div data-testid="support-success" className="rounded-xl border border-border bg-card p-8 text-center">
              <CheckCircle2 className="mx-auto h-12 w-12 text-brand" />
              <h3 className="mt-4 display-title text-3xl">Ticket Submitted</h3>
              <p className="mt-2 text-muted-foreground">Thanks! Our team will reach out using your preferred contact method.</p>
              <button onClick={() => setDone(false)} data-testid="support-new-ticket" className="mt-6 inline-flex items-center rounded-md border border-border px-6 py-3 text-sm font-semibold uppercase tracking-wide hover:bg-accent transition-colors">Submit Another</button>
            </div>
          ) : (
          <form onSubmit={submit} data-testid="support-form" className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div><Label htmlFor="s-first">First Name *</Label><Input id="s-first" data-testid="support-first-name" value={form.first_name} onChange={set("first_name")} className="mt-1.5" /></div>
              <div><Label htmlFor="s-last">Last Name *</Label><Input id="s-last" data-testid="support-last-name" value={form.last_name} onChange={set("last_name")} className="mt-1.5" /></div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div><Label htmlFor="s-email">Email *</Label><Input id="s-email" type="email" data-testid="support-email" value={form.email} onChange={set("email")} className="mt-1.5" /></div>
              <div><Label htmlFor="s-phone">Phone</Label><Input id="s-phone" data-testid="support-phone" value={form.phone} onChange={set("phone")} className="mt-1.5" /></div>
            </div>
            <div><Label htmlFor="s-client">Client / Athlete Name (if different)</Label><Input id="s-client" data-testid="support-client-name" value={form.client_name} onChange={set("client_name")} className="mt-1.5" /></div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <Label>Issue Category</Label>
                <Select value={form.category} onValueChange={set("category")}>
                  <SelectTrigger className="mt-1.5" data-testid="support-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Urgency</Label>
                <Select value={form.urgency} onValueChange={set("urgency")}>
                  <SelectTrigger className="mt-1.5" data-testid="support-urgency"><SelectValue /></SelectTrigger>
                  <SelectContent>{URGENCY.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label htmlFor="s-message">Message / Details *</Label><Textarea id="s-message" rows={5} data-testid="support-message" value={form.message} onChange={set("message")} className="mt-1.5" /></div>
            <div>
              <Label htmlFor="s-attach" className="flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> Attachment Link (optional)</Label>
              <Input id="s-attach" data-testid="support-attachment" placeholder="Paste a link to a screenshot or file" value={form.attachment_url} onChange={set("attachment_url")} className="mt-1.5" />
            </div>
            <div>
              <Label>Preferred Contact Method</Label>
              <Select value={form.preferred_contact} onValueChange={set("preferred_contact")}>
                <SelectTrigger className="mt-1.5" data-testid="support-preferred-contact"><SelectValue /></SelectTrigger>
                <SelectContent>{CONTACT_METHODS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <button type="submit" disabled={submitting} data-testid="support-submit-button" className="inline-flex items-center rounded-md bg-brand px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors disabled:opacity-60">{submitting ? "Submitting..." : "Submit Ticket"}</button>
            <p className="text-xs text-muted-foreground" data-testid="support-policy-note">
              For billing questions, review our{" "}
              <Link to="/policy/refund" className="text-brand hover:underline">Refund Policy</Link> and{" "}
              <Link to="/policy/cancellation" className="text-brand hover:underline">Cancellation Policy</Link>. Submitting a ticket is subject to our{" "}
              <Link to="/policy/privacy" className="text-brand hover:underline">Privacy Policy</Link>.
            </p>
          </form>
          )}
        </div>
      </section>
    </div>
  );
}
